import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { port } from '../App';
import HomeNavBar from '../Component/HomeComponent/HomeNavBar';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ArrowRight, CheckCircle2, Shield, Zap, Target, BarChart3, Users, Settings } from 'lucide-react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import CTAButton from '../Component/CTAButton';
import industryFocusedImg from '../Assest/cards/industry-focused.avif';

// industryImages imports
import itWorkforceImg from '../Assest/cards/industry/1/IT & Software Workforce Consulting Services.avif';
import healthcareWorkforceImg from '../Assest/cards/industry/1/Healthcare workforce Consulting Services.avif';
import manufacturingWorkforceImg from '../Assest/cards/industry/1/Manufacturing Workforce Consulting Solutions.avif';
import bfsiWorkforceImg from '../Assest/cards/industry/1/BFSI Workforce Consulting Services.avif';
import ecommerceWorkforceImg from '../Assest/cards/industry/1/Ecommerce Workforce Consulting Services.avif';
import logisticsWorkforceImg from '../Assest/cards/industry/1/Logistics & Supply Chain Workforce Consulting Services.avif';
import startupWorkforceImg from '../Assest/cards/industry/1/Startup Workforce Consulting Services.avif';
import gccWorkforceImg from '../Assest/cards/industry/1/Global Capability Center (GCC) Workforce Consulting Services.avif';

// differentiatorImages imports
import industrySpecializedDifferentiatorImg from '../Assest/cards/industry/2/Industry-Specialized Hiring Teams.avif';
import workforceInfraDifferentiatorImg from '../Assest/cards/industry/2/Workforce Infrastructure Thinking.avif';
import aiRecruitmentDifferentiatorImg from '../Assest/cards/industry/2/AI-Assisted Recruitment Intelligence.avif';
import integratedWorkforceDifferentiatorImg from '../Assest/cards/industry/2/Integrated Workforce Ecosystems.avif';
import builtForHighGrowthDifferentiatorImg from '../Assest/cards/industry/2/Built For High-Growth Organizations.avif';
import longTermPartnershipDifferentiatorImg from '../Assest/cards/industry/2/Long-Term Workforce Partnership Model.avif';
import industryImg from '../Assest/banners/Industries/industry.avif';

import itImg from '../Assest/cards/industry/1/IT & Software Workforce Consulting Services.avif';
import healthcareImg from '../Assest/cards/industry/1/Healthcare workforce Consulting Services.avif';
import manufacturingImg from '../Assest/cards/industry/1/Manufacturing Workforce Consulting Solutions.avif';
import bfsiImg from '../Assest/cards/industry/1/BFSI Workforce Consulting Services.avif';
import ecommerceImg from '../Assest/cards/industry/1/Ecommerce Workforce Consulting Services.avif';
import logisticsImg from '../Assest/cards/industry/1/Logistics & Supply Chain Workforce Consulting Services.avif';
import startupImg from '../Assest/cards/industry/1/Startup Workforce Consulting Services.avif';
import gccImg from '../Assest/cards/industry/1/Global Capability Center (GCC) Workforce Consulting Services.avif';

import diff1 from '../Assest/cards/industry/2/Industry-Specialized Hiring Teams.avif';
import diff2 from '../Assest/cards/industry/2/Workforce Infrastructure Thinking.avif';
import diff3 from '../Assest/cards/industry/2/AI-Assisted Recruitment Intelligence.avif';
import diff4 from '../Assest/cards/industry/2/Integrated Workforce Ecosystems.avif';
import diff5 from '../Assest/cards/industry/2/Built For High-Growth Organizations.avif';
import diff6 from '../Assest/cards/industry/2/Long-Term Workforce Partnership Model.avif';

const MainIndustryPage = () => {
    const navigate = useNavigate();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        pauseOnHover: true,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 1 } }
        ]
    };

    const differentiatorImages = [
        diff1,
        diff2,
        diff3,
        diff4,
        diff5,
        diff6
    ];

    const industryImages = [
        itImg,
        healthcareImg,
        manufacturingImg,
        bfsiImg,
        ecommerceImg,
        logisticsImg,
        startupImg,
        gccImg
    ];

    const handleFormChange = (e) => {
        let { name, value } = e.target;
        if (name === 'Phone Number') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            const payload = {
                ...formData,
                source_page: "Main Industry Page"
            };
            await axios.post(`${port}/api/page-enquiry/`, payload);
            toast.success("Enquiry submitted successfully! Our industry specialists will contact you shortly.");
            setFormData({});
            setIsFormOpen(false);
        } catch (error) {
            console.error("Error submitting industry enquiry:", error);
            toast.error("Failed to submit enquiry. Please try again.");
        } finally {
            setFormLoading(false);
        }
    };

    const openEnquiryModal = () => {
        setIsFormOpen(true);
    };

    // 8 supported industries list
    const industriesList = [
        {
            title: "IT & Software Workforce Consulting Services",
            slug: "information-technology",
            description: (
                <>
                    Technology businesses operate in one of the fastest-moving and most competitive workforce environments globally. Constant technology shifts, aggressive hiring demand, remote workforce expansion, <a href="https://hbr.org/2021/05/how-to-hire-a-leader" target="_blank" rel="noreferrer" className="text-blue-600 underline">leadership hiring</a> pressure, project-based recruitment, and specialized skill shortages create ongoing recruitment complexity.
                </>
            ),
            supportTitle: "How We Support IT Businesses",
            supportText: "As a trusted IT recruiters company in Bangalore, we help technology organizations build workforce systems capable of supporting rapid technical hiring, workforce continuity, project scalability, remote workforce operations, leadership hiring, and long-term recruitment efficiency.",
            supportPoints: [
                "Rapid technical hiring",
                "Workforce continuity",
                "Project scalability",
                "Remote workforce operations",
                "Leadership hiring",
                "Long-term recruitment efficiency"
            ],
            solutions: [
                "Technical Staffing",
                "Contract Staffing",
                "Permanent Hiring",
                "Project-Based Hiring",
                "Remote Hiring Solutions",
                "Leadership Hiring",
                "Recruitment Outsourcing",
                "Workforce Scalability Support"
            ],
            btnText: "Explore IT Workforce Solutions",
            icon: Zap,
            color: "blue"
        },
        {
            title: "Healthcare Workforce Consulting Services",
            slug: "healthcare",
             description: (
                <>
                    Healthcare organizations require workforce systems capable of balancing operational continuity, <a href="https://www.who.int/health-topics/health-workforce" target="_blank" rel="noreferrer" className="text-blue-600 underline">patient-care efficiency</a>, workforce availability, compliance governance, and specialized hiring precision. Healthcare workforce instability directly impacts operational performance and service delivery quality.
                </>
            ),
            supportTitle: "How We Support Healthcare Organizations",
            supportText: "We help hospitals, healthcare providers, clinics, and medical organizations improve workforce continuity, recruitment coordination, operational staffing efficiency, payroll management, and healthcare workforce scalability.",
            supportPoints: [
                "Workforce continuity",
                "Recruitment coordination",
                "Operational staffing efficiency",
                "Payroll management",
                "Healthcare workforce scalability"
            ],
            solutions: [
                "Healthcare Staffing",
                "Nursing Recruitment",
                "Administrative Hiring",
                "Operational Workforce Support",
                "Payroll Outsourcing",
                "Compliance Workforce Management",
                "Contract Staffing"
            ],
            btnText: "Explore Healthcare Workforce Solutions",
            icon: Users,
            color: "emerald"
        },
        {
            title: "Manufacturing Workforce Consulting Solutions",
            slug: "manufacturing",
            description: "Manufacturing businesses require workforce systems designed for operational continuity, shift-based staffing, industrial compliance, workforce productivity, and plant-level hiring scalability. Operational disruption caused by workforce gaps directly impacts productivity and business continuity.",
            supportTitle: "How We Support Manufacturing Businesses",
            supportText: "We help manufacturing organizations strengthen industrial workforce planning, operational staffing, workforce continuity, contract workforce management, and workforce scalability systems.",
            supportPoints: [
                "Industrial workforce planning",
                "Operational staffing",
                "Workforce continuity",
                "Contract workforce management",
                "Workforce scalability systems"
            ],
            solutions: [
                "Industrial Staffing",
                "Factory Workforce Hiring",
                "Contract Staffing",
                "Permanent Staffing",
                "Compliance Workforce Support",
                "Payroll Operations",
                "Operational Workforce Planning"
            ],
            btnText: "Explore Manufacturing Workforce Solutions",
            icon: Settings,
            color: "orange"
        },
        {
            title: "BFSI Workforce Consulting Services",
            slug: "bfsi",
            description: "BFSI organizations require highly structured workforce systems capable of supporting workforce governance, compliance alignment, operational confidentiality, leadership hiring precision, and long-term workforce stability.",
            supportTitle: "How We Support BFSI Organizations",
            supportText: "We help BFSI businesses improve workforce governance, operational hiring, leadership acquisition, workforce planning, and organizational hiring consistency.",
            supportPoints: [
                "Workforce governance",
                "Operational hiring",
                "Leadership acquisition",
                "Workforce planning",
                "Organizational hiring consistency"
            ],
            solutions: [
                "Banking Recruitment",
                "Financial Services Staffing",
                "Insurance Workforce Support",
                "Leadership Hiring",
                "Operational Workforce Management",
                "Payroll & Compliance Support"
            ],
            btnText: "Explore BFSI Workforce Solutions",
            icon: Shield,
            color: "indigo"
        },
        {
            title: "Ecommerce Workforce Consulting Services",
            slug: "ecommerce",
            description: "Ecommerce businesses operate with highly dynamic workforce demands driven by rapid scaling, warehouse operations, seasonal hiring fluctuations, customer support expansion, and operational workforce pressure.",
            supportTitle: "How We Support Ecommerce Businesses",
            supportText: "We help ecommerce organizations build workforce systems capable of supporting aggressive workforce scaling, operational staffing, warehouse hiring, customer support recruitment, and workforce continuity.",
            supportPoints: [
                "Aggressive workforce scaling",
                "Operational staffing",
                "Warehouse hiring",
                "Customer support recruitment",
                "Workforce continuity"
            ],
            solutions: [
                "Bulk Hiring",
                "Warehouse Staffing",
                "Operational Workforce Support",
                "Customer Support Hiring",
                "Contract Staffing",
                "Payroll Operations"
            ],
            btnText: "Explore Ecommerce Workforce Solutions",
            icon: Zap,
            color: "purple"
        },
        {
            title: "Logistics & Supply Chain Workforce Consulting Services",
            slug: "logistics",
            description: "Logistics and supply chain businesses require workforce systems capable of managing distributed workforce operations, high-volume staffing, shift-based hiring, operational continuity, and workforce coordination across multiple locations.",
            supportTitle: "How We Support Logistics Businesses",
            supportText: "We help logistics organizations strengthen workforce visibility, operational staffing, workforce scalability, distributed workforce coordination, and workforce continuity.",
            supportPoints: [
                "Workforce visibility",
                "Operational staffing",
                "Workforce scalability",
                "Distributed workforce coordination",
                "Workforce continuity"
            ],
            solutions: [
                "Supply Chain Staffing",
                "Warehouse Workforce Hiring",
                "Operational Staffing",
                "High-Volume Hiring",
                "Contract Staffing",
                "Compliance Workforce Support"
            ],
            btnText: "Explore Logistics Workforce Solutions",
            icon: BarChart3,
            color: "pink"
        },
        {
            title: "Startup Workforce Consulting Services",
            slug: "startups",
            description: "Startups often experience workforce growth faster than internal hiring infrastructure can support. Without structured workforce systems, recruitment scalability and operational stability quickly become major business challenges.",
            supportTitle: "How We Support Startups",
            supportText: "We help startups build scalable recruitment systems, workforce infrastructure, operational HR structure, leadership hiring capability, and workforce planning frameworks.",
            supportPoints: [
                "Scalable recruitment systems",
                "Workforce infrastructure",
                "Operational HR structure",
                "Leadership hiring capability",
                "Workforce planning frameworks"
            ],
            solutions: [
                "Startup Hiring",
                "Leadership Recruitment",
                "Recruitment Outsourcing",
                "Operational HR Support",
                "Payroll & Compliance Support",
                "Scalable Workforce Planning"
            ],
            btnText: "Explore Startup Workforce Solutions",
            icon: Target,
            color: "yellow"
        },
        {
            title: "Global Capability Center (GCC) Workforce Consulting Services",
            slug: "gcc",
            description: "GCC environments require highly specialized workforce systems capable of supporting large-scale hiring, operational workforce governance, leadership acquisition, workforce continuity, and long-term organizational scalability.",
            supportTitle: "How We Support GCC Environments",
            supportText: "We help GCC organizations strengthen workforce expansion, operational hiring scalability, multi-functional recruitment, leadership hiring, and workforce governance systems.",
            supportPoints: [
                "Workforce expansion",
                "Operational hiring scalability",
                "Multi-functional recruitment",
                "Leadership hiring",
                "Workforce governance systems"
            ],
            solutions: [
                "GCC Hiring",
                "Leadership Acquisition",
                "High-Volume Recruitment",
                "Operational Workforce Scaling",
                "Payroll & Compliance Support",
                "Recruitment Outsourcing"
            ],
            btnText: "Explore GCC Workforce Solutions",
            icon: Shield,
            color: "cyan"
        }
    ];

    // why consulting matters card data
    const consultingMatters = [
        {
            title: "Better Workforce Alignment",
            desc: "Industry-focused hiring improves workforce fit, operational relevance, and long-term employee alignment."
        },
        {
            title: "Faster Recruitment Execution",
            desc: "Sector-specific workforce intelligence improves sourcing speed and hiring coordination efficiency."
        },
        {
            title: "Improved Hiring Accuracy",
            desc: "Understanding industry workforce behavior improves candidate quality and recruitment precision."
        },
        {
            title: "Workforce Scalability",
            desc: "Structured workforce systems support expansion without operational hiring instability."
        },
        {
            title: "Operational Workforce Efficiency",
            desc: "Integrated HR systems reduce administrative complexity and improve workforce coordination."
        },
        {
            title: "Reduced Workforce Risk",
            desc: "Better payroll governance, compliance structure, and workforce visibility improve operational reliability."
        }
    ];

    // what makes us different
    const whyUsDifferent = [
        {
            title: "Industry-Specialized Hiring Teams",
            desc: "Dedicated workforce specialists aligned with sector-specific operational environments."
        },
        {
            title: "Workforce Infrastructure Thinking",
            desc: "Scalable workforce systems designed around operational growth instead of short-term staffing activity."
        },
        {
            title: "AI-Assisted Recruitment Intelligence",
            desc: "Technology-supported hiring operations improving recruitment visibility and sourcing coordination."
        },
        {
            title: "Integrated Workforce Ecosystems",
            desc: "Recruitment + Payroll + Compliance + Workforce Operations under one strategic framework."
        },
        {
            title: "Built For High-Growth Organizations",
            desc: "Designed specifically for startups, enterprises, GCCs, and rapidly scaling businesses."
        },
        {
            title: "Long-Term Workforce Partnership Model",
            desc: "Focused on sustainable workforce growth instead of transactional recruitment engagement."
        }
    ];

    // business outcomes
    const outcomesList = [
        {
            title: "Faster Hiring Execution",
            desc: "Structured recruitment systems reducing operational hiring delays."
        },
        {
            title: "Better Workforce Quality",
            desc: "Industry-aligned recruitment improving workforce relevance and hiring accuracy."
        },
        {
            title: "Workforce Scalability",
            desc: "High-volume workforce infrastructure supporting long-term organizational expansion."
        },
        {
            title: "Reduced Workforce Complexity",
            desc: "Integrated HR systems simplifying workforce coordination and operational management."
        },
        {
            title: "Payroll & Compliance Reliability",
            desc: "Improved workforce governance and operational workforce confidence."
        },
        {
            title: "Long-Term Workforce Stability",
            desc: "Strategic workforce systems supporting sustainable organizational growth."
        }
    ];

    return (
        <div className="bg-[#01014C] text-white min-h-screen relative">
           <Helmet>
    <title>Industries We Serve | Strategic Workforce Consulting</title>
    <meta name="description" content="Explore industry-focused HR consulting, staffing, recruitment outsourcing, payroll, and workforce management solutions for IT, Healthcare, Manufacturing, BFSI, Logistics, and Ecommerce enterprises." />
    <meta name="keywords" content="Industry-Focused HR Consulting Services, industry-focused HR consulting company, industry-specific staffing company in Bangalore, strategic workforce consulting company, IT staffing company in Bangalore, healthcare recruitment consulting company, manufacturing staffing solutions company, BFSI recruitment consulting firm, ecommerce workforce management company, logistics staffing company in Bangalore, startup hiring consulting company, GCC hiring company in Bangalore, AI-powered recruitment consulting company, workforce management company in Bangalore, staffing solutions company, recruitment process outsourcing company, HR outsourcing company in Bangalore, payroll outsourcing company, executive search firms in Bangalore, contract staffing company, permanent staffing company, high-volume hiring company, corporate HR solutions company, top recruitment firms in Bangalore, talent acquisition consulting company, HR compliance services company, best staffing company in Bangalore, industry-specialized recruitment agency, strategic workforce planning company, end-to-end HR solutions company, industry-focused HR consulting company in Bangalore for enterprises, best staffing company for IT and software workforce hiring, healthcare workforce consulting company for hospitals and clinics, manufacturing recruitment consulting company for industrial workforce scaling, BFSI staffing and recruitment company in Bangalore, ecommerce hiring solutions company for operational workforce growth, logistics and supply chain staffing company for high-volume hiring, startup hiring consulting company for rapid business expansion, GCC workforce consulting company in Bangalore, AI-powered recruitment company for enterprise workforce scaling, industry-specialized HR consulting services company, strategic workforce consulting company for multi-industry hiring, recruitment outsourcing company for operational workforce management, best HR consulting company in Bangalore for business growth, staffing company with payroll and compliance solutions, workforce management company in Bangalore for scalable operations" />
</Helmet>

            <div className="absolute w-full top-0 left-0 z-50">
                <HomeNavBar type="white" />
            </div>

            {/* HERO SECTION */}
            <section 
                className="relative pt-32 pb-24 overflow-hidden bg-cover bg-center bg-no-repeat"
                style={{ 
                    backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.55), rgba(1, 1, 60, 0.5)), url('${industryImg}')` 
                }}
            >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(37,99,235,0.15),transparent_70%)]" />
                <div className="container mx-auto px-6 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <span className="text-blue-400 font-black tracking-[0.3em] text-[10px] uppercase block mb-4">Sector Specialists</span>
                        <h1 className="text-3xl md:text-5xl font-bold mb-6 syne leading-tight">
                            Industry-Focused HR Consulting Services Built Around Real Industry Challenges
                        </h1>
                        <p className="text-blue-100/70 text-lg md:text-xl leading-relaxed mb-10 max-w-3xl mx-auto font-light">
                            A generic staffing model cannot solve <a href="/service" className="text-blue-400 underline">industry-specific workforce challenges</a>. Our industry-specialized recruitment teams understand the workforce realities businesses face inside each sector — allowing organizations to improve hiring speed, workforce quality, operational continuity, workforce governance, and long-term organizational scalability.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button 
                                onClick={openEnquiryModal}
                                className="px-8 py-4 bg-blue-600 hover:bg-white hover:text-[#01014C] text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 shadow-xl"
                            >
                                Discuss Industry Workforce Challenges
                            </button>
                            <button 
                                onClick={openEnquiryModal}
                                className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300"
                            >
                                Talk to Industry Experts
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY INDUSTRY CONSULTING MATTERS */}
            <section className="py-24 bg-[#01014C] border-t border-white/5 relative">
                <div className="container mx-auto px-6">
                    <div className=" mx-auto text-center mb-16">
                        <span className="text-blue-400 font-bold text-xs uppercase block mb-3">Strategic Insight</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne mb-6">
                            Why Industry-Specific Workforce Consulting Matters More Than Ever?
                        </h2>
                        <div className=" mx-auto space-y-4">
                             <p className="text-blue-100/70 text-base md:text-lg leading-relaxed">
                                The workforce challenges businesses face today are no longer universal. Yet many organizations still rely on <a href="/why-merida" className="text-blue-400 underline">generalized staffing approaches</a> that fail to understand sector-specific workforce realities.
                                 A specialized software engineer requires a completely different sourcing strategy than a healthcare professional, a manufacturing plant manager, or an e-commerce logistics leader. Generalist recruitment often leads to hiring delays, costly skill mismatches, and compliance risks. 
                            </p>
                           
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {consultingMatters.map((item, i) => (
                            <div key={i} className="p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:bg-white/10 transition-all duration-300">
                                <div className="w-10 h-10 bg-blue-600/20 text-blue-400 flex items-center justify-center rounded-xl mb-6 font-bold text-sm">
                                    0{i + 1}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                                <p className="text-blue-100/60 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <button 
                            onClick={openEnquiryModal}
                            className="px-8 py-4 bg-blue-600 hover:bg-white hover:text-[#01014C] text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 shadow-xl"
                        >
                            Talk To Workforce Strategists
                        </button>
                    </div>
                </div>
            </section>

            {/* INDUSTRIES WE SUPPORT */}
            <section className="py-24 bg-slate-50 relative border-y border-slate-200/50">
                <div className="container mx-auto px-6">
                    <div className=" mx-auto text-center mb-20">
                        <span className="text-blue-600 font-bold text-xs uppercase block mb-3">Sectors We Serve</span>
                        <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-6">
                            Industries We Support Through Strategic Workforce Consulting
                        </h2>
                        <p className="text-slate-500 leading-relaxed max-w-2xl mx-auto">
                            Our industry hiring specialists understand the operational workforce dynamics businesses face inside each environment — allowing organizations to scale more efficiently while reducing workforce complexity.
                        </p>
                    </div>

                    <div className="space-y-16 max-w-6xl mx-auto">
                        {industriesList.map((ind, i) => {
                            const isWhiteCard = i % 2 === 0;
                            const IconComponent = ind.icon;
                            
                            // Dynamic color classes for icon wrapper
                            const iconWrapperClass = (() => {
                                const colors = {
                                    blue: isWhiteCard ? 'bg-blue-50 text-blue-600' : 'bg-white/5 text-blue-400',
                                    emerald: isWhiteCard ? 'bg-emerald-50 text-emerald-600' : 'bg-white/5 text-emerald-400',
                                    orange: isWhiteCard ? 'bg-orange-50 text-orange-600' : 'bg-white/5 text-orange-400',
                                    indigo: isWhiteCard ? 'bg-indigo-50 text-indigo-600' : 'bg-white/5 text-indigo-400',
                                    purple: isWhiteCard ? 'bg-purple-50 text-purple-600' : 'bg-white/5 text-purple-400',
                                    pink: isWhiteCard ? 'bg-pink-50 text-pink-600' : 'bg-white/5 text-pink-400',
                                    yellow: isWhiteCard ? 'bg-yellow-50 text-yellow-600' : 'bg-white/5 text-yellow-400',
                                    cyan: isWhiteCard ? 'bg-cyan-50 text-cyan-600' : 'bg-white/5 text-cyan-400',
                                };
                                return colors[ind.color] || (isWhiteCard ? 'bg-slate-100 text-slate-600' : 'bg-white/5 text-blue-400');
                            })();

                            return (
                                <motion.div 
                                    key={i} 
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    whileHover={{ y: -8 }}
                                    className={`p-8 md:p-12 rounded-[3rem] shadow-xl hover:shadow-2xl border transition-shadow duration-300 ${
                                        isWhiteCard 
                                            ? 'bg-white border-slate-200/60 text-slate-800' 
                                            : 'bg-[#01014C] border-white/5 text-white'
                                    }`}
                                >
                                    <div className={`flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8 pb-8 border-b ${
                                        isWhiteCard ? 'border-slate-100' : 'border-white/5'
                                    }`}>
                                        <div className="flex items-center gap-4">
                                            <div className={`p-4 rounded-2xl ${iconWrapperClass}`}>
                                                <IconComponent className="w-8 h-8" />
                                            </div>
                                            <h3 className={`text-xl md:text-2xl font-bold syne text-left ${
                                                isWhiteCard ? 'text-[#01014C]' : 'text-white'
                                            }`}>{ind.title}</h3>
                                        </div>
                                        <button 
                                            onClick={() => navigate(`/industries/${ind.slug}`)}
                                            className={`px-6 py-3 font-bold uppercase tracking-wider text-[10px] rounded-xl transition-all duration-300 whitespace-nowrap ${
                                                isWhiteCard
                                                    ? 'bg-blue-600 hover:bg-[#01014C] text-white shadow-md'
                                                    : 'bg-blue-600 hover:bg-white hover:text-[#01014C] text-white'
                                            }`}
                                        >
                                            {ind.btnText}
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 text-left items-stretch">
                                        {/* Left Side Content */}
                                        <div className="space-y-8 flex flex-col justify-center">
                                            <p className={`leading-relaxed text-base md:text-lg ${
                                                isWhiteCard ? 'text-slate-600' : 'text-blue-100/80'
                                            }`}>{ind.description}</p>

                                            <div className="space-y-4">
                                                <h4 className={`font-bold text-sm uppercase tracking-wider ${
                                                    isWhiteCard ? 'text-slate-800' : 'text-white'
                                                }`}>Strategic Solutions Offered:</h4>
                                                <div className="flex flex-wrap gap-2.5">
                                                    {ind.solutions.map((sol, j) => (
                                                        <span 
                                                            key={j} 
                                                            className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all duration-300 ${
                                                                isWhiteCard
                                                                    ? 'bg-slate-50 border-slate-200/60 text-slate-700 hover:border-blue-600/30 hover:bg-white'
                                                                    : 'bg-white/5 border-white/5 text-blue-100/80 hover:border-blue-500/30'
                                                            }`}
                                                        >
                                                            {sol}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Side Image - full height */}
                                        <div className="overflow-hidden rounded-3xl w-full h-full min-h-[350px] lg:min-h-full border border-slate-200/10 relative shadow-md">
                                            <img 
                                                src={industryImages[i]} 
                                                alt={ind.title} 
                                                className="w-full h-full object-cover hover:scale-105 transition-all duration-500 opacity-90 hover:opacity-100" 
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHAT MAKES US DIFFERENT */}
            <section className="py-10 bg-[#2563EB] relative overflow-hidden">
                <div className="container mx-auto px-6">
                    <div className=" mx-auto text-center mb-16">
                        <span className="text-blue-100 font-bold  text-xs uppercase block mb-3">Our Differentiators</span>
                        <h2 className="text-3xl md:text-4xl font-bold syne text-white mb-6">
                            What Makes Our Industry Workforce Consulting Approach Different?
                        </h2>
                        <p className="text-blue-50 leading-relaxed font-light">
                            Merida HR Consulting operates with industry-specific workforce intelligence designed around how businesses actually function operationally.
                        </p>
                    </div>

                    <div className="mb-16">
                        <Slider {...sliderSettings} className="differentiators-slider">
                            {whyUsDifferent.map((item, i) => (
                                <div key={i} className="px-3 pb-4">
                                    <div className="aspect-square bg-white border border-transparent rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col text-left shadow-md group">
                                        <div className="flex-1 min-h-0 w-full overflow-hidden bg-slate-50 relative">
                                            <div className="absolute top-4 left-4 z-20 w-10 h-10 bg-white/90 backdrop-blur-sm text-blue-600 flex items-center justify-center rounded-xl shadow-sm">
                                                {i === 0 && <Users className="w-5 h-5" />}
                                                {i === 1 && <Settings className="w-5 h-5" />}
                                                {i === 2 && <Zap className="w-5 h-5" />}
                                                {i === 3 && <Shield className="w-5 h-5" />}
                                                {i === 4 && <Target className="w-5 h-5" />}
                                                {i === 5 && <CheckCircle2 className="w-5 h-5" />}
                                            </div>
                                            <div className="absolute top-4 right-4 z-20">
                                                <span className="text-[10px] font-bold text-white bg-black/40 backdrop-blur-md px-2 py-1 rounded-md">0{i + 1}</span>
                                            </div>
                                            <img 
                                                src={differentiatorImages[i]} 
                                                alt={item.title} 
                                                className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-transform duration-500" 
                                            />
                                        </div>
                                        
                                        <div className="p-4 space-y-1.5 bg-white z-10 relative">
                                            <h3 className="text-base font-bold text-[#01014C] leading-snug">{item.title}</h3>
                                            <p className="text-slate-500 text-xs leading-relaxed line-clamp-2">{item.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>

                    <div className="text-center mt-12">
                        <button 
                            onClick={openEnquiryModal}
                            className="px-8 py-4 bg-[#01014C] hover:bg-white hover:text-[#01014C] text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 shadow-xl"
                        >
                            Build Smarter Workforce Systems
                        </button>
                    </div>
                </div>
                <style>{`
                    .differentiators-slider .slick-track {
                        display: flex !important;
                    }
                    .differentiators-slider .slick-slide {
                        height: auto;
                    }
                    .differentiators-slider .slick-dots li button:before {
                        color: rgba(255, 255, 255, 0.4) !important;
                    }
                    .differentiators-slider .slick-dots li.slick-active button:before {
                        color: #ffffff !important;
                    }
                `}</style>
            </section>

            {/* BUSINESS OUTCOMES */}
            <section className="py-10 bg-white relative">
                <div className="container mx-auto px-6">
                    <div className=" mx-auto text-center mb-16">
                        <span className="text-blue-600 font-bold text-xs uppercase block mb-3">Outcomes Delivered</span>
                        <h2 className="text-3xl md:text-5xl font-bold syne text-[#01014C] mb-4">
                            Business Outcomes We Help Organizations Achieve Across Industries
                        </h2>
                        <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                            Discover the measurable efficiency, compliance, and growth metrics that validate our specialized sector-focused model.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {outcomesList.map((item, i) => (
                            <div key={i} className="p-8 bg-blue-900 border border-transparent rounded-[2.5rem] hover:bg-blue-800 hover:shadow-2xl transition-all duration-300">
                                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-blue-300 flex-shrink-0" />
                                    {item.title}
                                </h3>
                                <p className="text-blue-100/80 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <button 
                            onClick={openEnquiryModal}
                            className="px-8 py-4 bg-blue-600 hover:bg-white hover:text-[#01014C] text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 shadow-xl"
                        >
                            Connect With Industry Workforce Specialists
                        </button>
                    </div>
                </div>
            </section>

            {/* FINAL CTA REPLACED WITH BANNER */}
            <section className="py-16 bg-white relative overflow-hidden border-t border-slate-100">
                <div className="container mx-auto px-6 relative z-10">
                    <div className=" mx-auto text-center">
                        <div className="w-full rounded-[2rem] overflow-hidden shadow-2xl mb-10 border border-slate-200">
                            <img 
                                src={industryFocusedImg} 
                                alt="Industry Focused Workforce Consulting" 
                                className="w-full h-auto object-cover"
                            />
                        </div>
                        <div className="flex justify-center">
                            <CTAButton
                                text="Book Strategic Workforce Consultation"
                                onClick={openEnquiryModal}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Lead Enquiry Popup Modal */}
            <AnimatePresence>
                {isFormOpen && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsFormOpen(false)}
                            className="absolute inset-0 bg-[#01014c]/80 backdrop-blur-md"
                        />
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="relative bg-[#01014C] border border-white/10 rounded-[3rem] p-8 md:p-12 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto z-10 custom-scrollbar text-left text-white"
                        >
                            <button 
                                onClick={() => setIsFormOpen(false)}
                                className="absolute top-6 right-6 text-white/60 hover:text-white bg-white/5 hover:bg-white/10 p-3 rounded-full transition-colors z-20"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <div className="max-w-2xl mb-8 pr-12">
                                <h2 className="text-2xl md:text-3xl font-bold text-white syne mb-4">Discuss Industry Workforce Challenges</h2>
                                <p className="text-blue-100/60 leading-relaxed text-sm md:text-base">
                                    Connect with our specialized consulting team to align your workforce structure, screening parameters, and hiring timeline with sector objectives.
                                </p>
                            </div>

                            <form className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-8" onSubmit={handleFormSubmit}>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Full Name</label>
                                    <input 
                                        type="text" 
                                        name="Full Name"
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Full Name" 
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Company Name</label>
                                    <input 
                                        type="text" 
                                        name="Company Name"
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Company Name" 
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Business Email</label>
                                    <input 
                                        type="email" 
                                        name="Business Email"
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Business Email" 
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Phone Number</label>
                                    <input 
                                        type="tel" 
                                        name="Phone Number"
                                        value={formData['Phone Number'] || ''}
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Phone Number" 
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all placeholder:text-white/20 text-sm" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Industry Type</label>
                                    <select
                                        name="Industry Type"
                                        onChange={handleFormChange}
                                        required
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer"
                                    >
                                        <option value="" disabled selected className="bg-[#01014C] text-white/50">Select Industry</option>
                                        <option value="IT & Software" className="bg-[#01014C]">IT & Software</option>
                                        <option value="Healthcare" className="bg-[#01014C]">Healthcare</option>
                                        <option value="Manufacturing" className="bg-[#01014C]">Manufacturing</option>
                                        <option value="BFSI" className="bg-[#01014C]">BFSI</option>
                                        <option value="Ecommerce & Retail" className="bg-[#01014C]">Ecommerce & Retail</option>
                                        <option value="Logistics & Supply Chain" className="bg-[#01014C]">Logistics & Supply Chain</option>
                                        <option value="Startups" className="bg-[#01014C]">Startups</option>
                                        <option value="GCCs" className="bg-[#01014C]">GCCs</option>
                                        <option value="Other" className="bg-[#01014C]">Other</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2 lg:col-span-3 space-y-2">
                                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest ml-1">Message</label>
                                    <textarea 
                                        rows={3} 
                                        name="Message"
                                        onChange={handleFormChange}
                                        placeholder="Briefly describe your requirements..."
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-blue-500 transition-all text-sm placeholder:text-white/20" 
                                    />
                                </div>
                                <button 
                                    type="submit" 
                                    disabled={formLoading}
                                    className={`lg:col-span-3 py-4 mt-2 ${formLoading ? 'bg-blue-700 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-white hover:text-[#01014C] text-white'} font-black uppercase tracking-widest rounded-xl transition-all shadow-xl flex justify-center items-center text-sm`}
                                >
                                    {formLoading ? (
                                        <span className="animate-pulse">Submitting Request...</span>
                                    ) : (
                                        "Request Industry Consultation"
                                    )}
                                </button>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MainIndustryPage;
